import { useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Complaints.module.css';
import ComplaintsSkeleton from './ComplaintsSkeleton';
import { getComplaintsTable } from '../ComplaintsApi';
import { useQuery } from '@tanstack/react-query';
import ComplaintsTable from './ComplaintsTable';
import AddNewComplaint from '../AddNewComplaint/AddNewComplaint';
import { STATUS_COLORS } from '../complaintsHandler';
import Error from '../Error';
import BackSection from './../../Services/BackSection';

// ── Animation Variants ────────────────────────────────────────────────────
const pageVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut', staggerChildren: 0.07 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.06, duration: 0.3, ease: 'easeOut' },
  }),
};

// ── Stat Card ─────────────────────────────────────────────────────────────
function StatCard({ label, value, color, index }) {
  return (
    <motion.div
      className={styles.statCard}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue} style={color ? { color } : undefined}>
        {value}
      </div>
    </motion.div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────
export default function Complaints() {
  // ── All hooks first, unconditionally ─────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['tableComplaints'],
    queryFn: getComplaintsTable,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Safe fallback while data is loading
  const table = data?.table ?? [];

  const rows = useMemo(() => {
    return table.filter((c) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        (c.Name?.value ?? '').toLowerCase().includes(q) ||
        (c['PIRM_Base__Company__c']?.value ?? '').toLowerCase().includes(q) ||
        (c['PIRM_Base__Product__c']?.value ?? '').toLowerCase().includes(q) ||
        (c['PIRM_Base__Issue__c']?.value ?? '').toLowerCase().includes(q);
      const matchStatus =
        !statusFilter || c['PIRM_Base__Status__c']?.value === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [table, search, statusFilter]);

  const statusCounts = useMemo(() => {
    return table.reduce((acc, c) => {
      const status = c['PIRM_Base__Status__c']?.value;
      if (status) acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [table]);

  const total = table.length;

  // ── Guards after all hooks ────────────────────────────────────────────
  if (isLoading) return <ComplaintsSkeleton />;
  if (error) {
    const errorMsg = error?.message || 'Please contact your PIRM administrator if there is an issue.';
    return <Error errorMsg={errorMsg} />; // Removed the extra ';'
  }

  return (
    <motion.div
      className={styles.page}
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      <BackSection pageName="Complaints" to="/policies" />
      {/* Page Header */}
      <motion.div className={`${styles.pageHeader} mt-3`} variants={itemVariants}>
        <h1>Complaints</h1>
        <motion.button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => setModalOpen(true)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Complaint
        </motion.button>
      </motion.div>

      {/* Add New Complaint Modal */}
      <AddNewComplaint
        createFields={data?.createFields ?? []}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(newComplaint) => {
          console.log('Created:', newComplaint);
          setModalOpen(false);
        }}
      />

      {/* Stats Row */}
      <motion.div className={styles.statsRow} variants={itemVariants}>
        <StatCard label="Total Complaints" value={total} index={0} />
        {Object.entries(statusCounts).map(([status, count], i) => (
          <StatCard
            key={status}
            label={status}
            value={count}
            color={STATUS_COLORS[status] ?? 'var(--info)'}
            index={i + 1}
          />
        ))}
      </motion.div>

      {/* Filter Bar */}
      <motion.div className={styles.filterBar} variants={itemVariants}>
        <div className={styles.searchWrap}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by ticket #, company, product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {Object.keys(statusCounts).map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </motion.div>

      {/* Table */}
      <motion.div variants={itemVariants}>
        <ComplaintsTable
          rows={rows}
          search={search}
          statusFilter={statusFilter}
          STATUS_COLORS={STATUS_COLORS}
        />
      </motion.div>
    </motion.div>
  );
}